// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LinesHeader, { LinesHeaderStyles } from "@conklin/App/Components/Cart/LinesHeader";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import { parseBoolean, pluralize } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartLineCardExpanded, {
    CartLineCardExpandedStyles,
} from "@insite/content-library/Widgets/Cart/CartLineCardExpanded";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // XNG-Changes
    isAuthenticated: xIsAuthenticated(state),
    isAllProductsInUrl: parseQueryString<{ allproducts?: string }>(getLocation(state)?.search)?.allproducts,
});

const mapDispatchToProps = {
    updateCartLine,
    removeCartLine,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & WidgetProps;

export interface RfqRequestQuoteProductListStyles {
    lineCountWrapper?: InjectableCss;
    lineCountStyles?: TypographyPresentationProps;
    cartLineStyles?: CartLineCardExpandedStyles;
    // XNG-Changes
    linesHeader?: LinesHeaderStyles;
}

export const rfqRequestQuoteProductListStyles: RfqRequestQuoteProductListStyles = {
    lineCountWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 20px;
        `,
    },
    lineCountStyles: { weight: 600 },
};

const styles = rfqRequestQuoteProductListStyles;

const RfqRequestQuoteProductList: FC<Props> = ({
    cart,
    productSettings,
    showInventory,
    updateCartLine,
    removeCartLine,
    // XNG-Changes
    isAuthenticated,
    isAllProductsInUrl,
}) => {
    if (!cart || !cart.cartLines) {
        return null;
    }

    const displayAllCartlines = !isAuthenticated && isAllProductsInUrl === "true";

    // XNG-Changes
    const cartLines = displayAllCartlines
        ? cart.cartLines || []
        : cart.cartLines?.filter(
              l =>
                  l.quoteRequired ||
                  (!isAuthenticated && parseBoolean(l.properties.authRequiredForPurchase)) ||
                  parseBoolean(l.properties.isQuote),
          );

    // XNG-Changes LinesHeader
    return (
        <>
            <StyledWrapper {...styles.lineCountWrapper}>
                {/*  XNG-Changes: adding pluralize */}
                <Typography {...styles.lineCountStyles}>
                    {pluralize(cartLines.length, translate("Product"), { showNumber: true })}
                </Typography>
            </StyledWrapper>
            <LinesHeader rightTitles={["Inventory", "QTY", "Total"]} extendedStyles={styles.linesHeader} />
            {cartLines.map(cartLine => {
                const showRemoveAction = !cartLine.isPromotionItem && cart.canModifyOrder;
                return (
                    <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                        <CartLineCardExpanded
                            cart={cart}
                            promotions={[]}
                            productSettings={productSettings}
                            showInventoryAvailability={showInventory}
                            showLineNotes={true}
                            extendedStyles={styles.cartLineStyles}
                            updateCartLine={updateCartLine}
                            removeCartLine={removeCartLine}
                            showRemoveAction={showRemoveAction}
                            hideAddToList={true}
                            // XNG-Changes
                            {...{ isEditableQTY: false }}
                        />
                    </CartLineContext.Provider>
                );
            })}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqRequestQuoteProductList),
    definition: {
        group: "RFQ Request Quote",
        displayName: "Product List",
        allowedContexts: ["RfqRequestQuotePage", "RequestQuoteFormPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
